import { useEffect, useRef, useState } from "preact/hooks";
import type { Import } from "../lib/types";
import {
  parsePocketFile,
  importPocketRows,
  listImports,
  importExtractionProgress,
  type ImportProgress,
  type ImportItemSummary,
} from "../lib/pocket-import";

export function ImportPanel() {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<ImportProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [imports, setImports] = useState<Import[]>([]);
  const [summaries, setSummaries] = useState<Record<string, ImportItemSummary[]>>({});
  const inputRef = useRef<HTMLInputElement>(null);

  const reload = async () => {
    try {
      const imps = await listImports();
      setImports(imps);
      const sums: Record<string, ImportItemSummary[]> = {};
      for (const i of imps) sums[i.id] = await importExtractionProgress(i.id);
      setSummaries(sums);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  useEffect(() => { void reload(); }, []);

  const onFile = async (e: Event) => {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    setBusy(true);
    setError(null);
    setProgress({ processed: 0, total: 0, inserted: 0, duplicates: 0, failed: 0 });
    try {
      const { rows, filename } = await parsePocketFile(file);
      if (rows.length === 0) throw new Error("No Pocket rows found in file.");
      await importPocketRows(rows, filename, setProgress);
      await reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const pct = progress && progress.total > 0 ? Math.round((progress.processed / progress.total) * 100) : 0;

  return (
    <div class="col">
      {error && <div class="banner error">{error}</div>}

      <div class="row">
        <input ref={inputRef} type="file" accept=".zip,.csv" onChange={onFile} disabled={busy}
          style="flex: 1; min-height: auto; padding: var(--space-1);" />
      </div>
      <p class="subtle" style="font-size: var(--fs-12);">
        Upload a Pocket CSV or a ZIP of CSV parts. Articles are inserted as
        pending and extracted in the background by the queue — close the tab
        freely; the import resumes.
      </p>

      {busy && progress && (
        <div class="col" style="margin-top: var(--space-3);">
          <div class="spread" style="font-size: var(--fs-14);">
            <span>{progress.processed} / {progress.total}</span>
            <span class="muted">{pct}%</span>
          </div>
          <div class="progress"><span style={`width: ${pct}%`} /></div>
          <div class="row subtle" style="font-size: var(--fs-12);">
            <span>inserted {progress.inserted}</span>
            <span>· duplicates {progress.duplicates}</span>
            <span>· failed {progress.failed}</span>
          </div>
        </div>
      )}

      {imports.length > 0 && (
        <div class="col" style="margin-top: var(--space-5);">
          <h3 style="margin: 0; font-size: var(--fs-14); font-weight: 600;">Past imports</h3>
          {imports.map((i) => (
            <div key={i.id} class="card" style="padding: var(--space-3); margin: 0;">
              <div class="spread">
                <strong>{i.filename}</strong>
                <span class="subtle" style="font-size: var(--fs-12);">{i.status}</span>
              </div>
              <div class="row subtle" style="font-size: var(--fs-12); margin-top: var(--space-2);">
                <span>total {i.total}</span>
                <span>· inserted {i.inserted}</span>
                <span>· duplicates {Math.max(0, i.total - i.inserted - i.failed)}</span>
                <span>· failed {i.failed}</span>
              </div>
              {(summaries[i.id] ?? []).length > 0 && (
                <div class="row subtle" style="font-size: var(--fs-12); margin-top: var(--space-1); flex-wrap: wrap;">
                  <span class="chip">extraction</span>
                  {summaries[i.id].map((s) => <span class="chip">{s.status}: {s.count}</span>)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
